/**
 * Settings Feature Models
 *
 * Types and constants for the settings view: user preference state,
 * account display info, theme modes, and validation bounds.
 *
 * NOTE (WI-052 boundary): The shared `UserSettings` interface
 * (@shared/models/user.models) does not yet include `analyticsEnabled`
 * (GDPR/CCPA analytics opt-out). Extending the shared model is outside this
 * work item's file boundary, so the settings feature defines its own
 * superset type (`AppUserSettings`) and reads the field defensively.
 *
 * @see WI-052 - Settings View
 * @see data-schemas.md - User.settings schema
 */

import { UserSettings } from '@shared/models/user.models';

/**
 * Theme mode preference.
 * - 'auto': follow system `prefers-color-scheme`
 * - 'light' / 'dark': explicit user override
 */
export type ThemeMode = 'auto' | 'light' | 'dark';

/**
 * User settings as managed by the settings view.
 * Superset of the shared UserSettings with the analytics opt-out flag.
 */
export interface AppUserSettings extends UserSettings {
  /** Analytics opt-out (GDPR/CCPA). Default: true (enabled). */
  analyticsEnabled: boolean;
}

/**
 * Read-only account information shown in the profile section.
 */
export interface AccountInfo {
  name: string;
  phone: string;
  email?: string;
  subscriptionTier: 'free' | 'premium';
}

/** localStorage key for the theme preference (spec WI-052 Section 5.3). */
export const THEME_STORAGE_KEY = 'checkerfly-theme-preference';

/** Grace window bounds in minutes (spec WI-052 Section 1.3). */
export const GRACE_WINDOW_MIN = 5;
export const GRACE_WINDOW_MAX = 120;

/** Nudge delay bounds in minutes (spec WI-052 Section 1.3). */
export const NUDGE_DELAY_MIN = 5;
export const NUDGE_DELAY_MAX = 60;

/** HH:MM 24-hour time format used by quiet hours. */
export const TIME_FORMAT_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Displayed app version (About section). */
export const APP_VERSION = '1.0.0';
